import { useCallback, useState } from 'react';
import { Alert, Empty, Space, Tabs, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { Loader } from '~/app/components/common/Loader/Loader';
import {
  COMMAND_EXECUTE_ACCENT,
  COMMAND_EXECUTE_ACCENT_HOVER,
} from '~/app/components/asset-detail/AssetDetailCommandsTab/styles';
import { AssetOcppConfigurationFetchedAt } from './components/AssetOcppConfigurationFetchedAt/AssetOcppConfigurationFetchedAt';
import { useAssetOcppConfiguration } from './hooks/useAssetOcppConfiguration';
import { useAssetOcppConfigurationViewTabs } from './hooks/useAssetOcppConfigurationViewTabs';
import styles from './AssetDetailOcppConfigurationTab.module.css';

export interface AssetDetailOcppConfigurationTabProps {
  organizationId: string;
  assetId: string;
  canRefresh: boolean;
}

type ConfigurationViewKey = 'table' | 'json';

export function AssetDetailOcppConfigurationTab({
  organizationId,
  assetId,
  canRefresh,
}: AssetDetailOcppConfigurationTabProps) {
  const { t } = useTranslation('assets');
  const [activeView, setActiveView] = useState<ConfigurationViewKey>('table');
  const {
    search,
    setSearch,
    rows,
    snapshot,
    fetchedAt,
    isLoading,
    isError,
    error,
    refetch,
    refresh,
    isRefreshing,
  } = useAssetOcppConfiguration(organizationId, assetId);

  const onSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch],
  );

  const viewTabItems = useAssetOcppConfigurationViewTabs({
    rows,
    snapshot,
    search,
    onSearchChange,
  });

  const hasSavedSnapshot = fetchedAt != null;
  const showNeverFetchedEmpty = !hasSavedSnapshot && !isLoading && !isError;
  const errorMessage =
    error instanceof Error
      ? error.message
      : t(
          'detail.ocppConfiguration.loadFailed',
          'Failed to load OCPP configuration',
        );

  const refreshButton = canRefresh ? (
    <Button
      type="primary"
      size="large"
      icon={<ReloadOutlined />}
      loading={isRefreshing}
      disabled={isRefreshing}
      onClick={() => refresh()}
      className={styles.refreshButton}
      style={{
        background: COMMAND_EXECUTE_ACCENT,
        borderColor: COMMAND_EXECUTE_ACCENT,
      }}
      onMouseEnter={(event) => {
        if (!isRefreshing) {
          event.currentTarget.style.background = COMMAND_EXECUTE_ACCENT_HOVER;
          event.currentTarget.style.borderColor = COMMAND_EXECUTE_ACCENT_HOVER;
        }
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = COMMAND_EXECUTE_ACCENT;
        event.currentTarget.style.borderColor = COMMAND_EXECUTE_ACCENT;
      }}
    >
      {t('detail.ocppConfiguration.refresh', 'Refresh from charger')}
    </Button>
  ) : null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space
        align="start"
        style={{ width: '100%', justifyContent: 'space-between' }}
        wrap
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('detail.ocppConfiguration.title', 'OCPP Configuration')}
          </Typography.Title>
          <AssetOcppConfigurationFetchedAt fetchedAt={fetchedAt} />
        </div>
        {refreshButton}
      </Space>

      {snapshot?.possiblyIncomplete ? (
        <Alert
          type="info"
          showIcon
          message={t(
            'detail.ocppConfiguration.possiblyIncompleteTitle',
            'Snapshot may be incomplete',
          )}
          description={t(
            'detail.ocppConfiguration.possiblyIncompleteDescription',
            'The charger may limit how many keys it returns (GetConfigurationMaxKeys). Values shown are the last successful response as returned.',
          )}
        />
      ) : null}

      {isError && !hasSavedSnapshot ? (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          action={
            <Button size="small" onClick={() => void refetch()}>
              {t('detail.ocppConfiguration.retry', 'Retry')}
            </Button>
          }
        />
      ) : null}

      {isLoading && !hasSavedSnapshot ? <Loader /> : null}

      {showNeverFetchedEmpty ? (
        <Empty
          description={t(
            'detail.ocppConfiguration.neverFetchedEmpty',
            'No OCPP configuration has been fetched for this asset yet.',
          )}
        >
          {canRefresh ? (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={isRefreshing}
              disabled={isRefreshing}
              onClick={() => refresh()}
            >
              {t('detail.ocppConfiguration.refresh', 'Refresh from charger')}
            </Button>
          ) : null}
        </Empty>
      ) : null}

      {hasSavedSnapshot ? (
        <Tabs
          activeKey={activeView}
          onChange={(key) => setActiveView(key as ConfigurationViewKey)}
          items={viewTabItems}
          destroyOnHidden={false}
        />
      ) : null}
    </Space>
  );
}
